import React, { useState, useEffect, useCallback } from 'react';
import { expensesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { fmt } from '../lib/currency';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Search, 
  Check, 
  X,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  FileImage,
  File,
  MapPin,
  Calendar,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Safe receipt image component - avoids innerHTML XSS vulnerability
function ReceiptImage({ src, alt }) {
  const [hasError, setHasError] = useState(false);
  
  if (hasError) {
    return <p className="text-red-400">Failed to load image</p>;
  }
  
  return (
    <img
      src={src}
      alt={alt}
      className="max-w-full max-h-[400px] object-contain rounded"
      onError={() => setHasError(true)}
    />
  );
}

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/20',
};

export default function Expenses() {
  const { user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchExpenses = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await expensesAPI.list(params);
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleApprove = async (id) => {
    try {
      setProcessing(true);
      await expensesAPI.update(id, { status: 'approved', reviewer_notes: reviewNotes || null });
      toast.success('Expense approved');
      setShowReviewModal(false);
      setSelectedExpense(null);
      setReviewNotes('');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to approve expense');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (id) => {
    if (!reviewNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      setProcessing(true);
      await expensesAPI.update(id, { status: 'rejected', reviewer_notes: reviewNotes });
      toast.success('Expense rejected');
      setShowReviewModal(false);
      setSelectedExpense(null);
      setReviewNotes('');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to reject expense');
    } finally {
      setProcessing(false);
    }
  };

  const openReviewModal = (expense) => {
    setSelectedExpense(expense);
    setReviewNotes('');
    setShowReviewModal(true);
  };

  const openReceiptModal = (expense) => {
    setSelectedExpense(expense);
    setShowReceiptModal(true);
  };

  const getReceiptUrl = (expense) => {
    // URL without token - the httpOnly cookie will be used for auth
    return expensesAPI.getReceiptUrl(expense.id);
  };

  const filteredExpenses = expenses.filter(expense => 
    expense.medic_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    expense.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    pending: expenses.filter(e => e.status === 'pending').reduce((acc, e) => acc + e.amount, 0),
    approved: expenses.filter(e => e.status === 'approved').reduce((acc, e) => acc + e.amount, 0),
    rejected: expenses.filter(e => e.status === 'rejected').reduce((acc, e) => acc + e.amount, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-container">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                <Clock className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Pending</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {fmt(stats.pending, orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Approved</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {fmt(stats.approved, orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/15">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Rejected</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {fmt(stats.rejected, orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
          <Input
            placeholder="Search by medic or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#2B2B2B] border-white/10"
            data-testid="search-expenses-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] bg-[#2B2B2B] border-white/10" data-testid="expense-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#2B2B2B] border-white/10">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Expenses Table */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                <TableHead className="text-[#A1A1AA]">Category</TableHead>
                <TableHead className="text-[#A1A1AA]">Amount</TableHead>
                <TableHead className="text-[#A1A1AA]">Shift</TableHead>
                <TableHead className="text-[#A1A1AA]">Date</TableHead>
                <TableHead className="text-[#A1A1AA]">Receipt</TableHead>
                <TableHead className="text-[#A1A1AA]">Status</TableHead>
                <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-[#A1A1AA] py-8">
                    No expenses found
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => (
                  <TableRow 
                    key={expense.id} 
                    className="border-white/5 hover:bg-white/5"
                    data-testid={`expense-row-${expense.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-[#A1A1AA]" />
                        <span className="text-[#F2F2F2] font-medium">{expense.medic_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#A1A1AA]">{expense.category}</TableCell>
                    <TableCell className="text-[#1ABC9C] font-['JetBrains_Mono'] font-medium">
                      {fmt(expense.amount, expense.currency)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {expense.shift_location && (
                          <div className="flex items-center gap-1 text-[#A1A1AA]">
                            <MapPin className="w-3 h-3" />
                            {expense.shift_location}
                          </div>
                        )}
                        {expense.shift_date && (
                          <div className="flex items-center gap-1 text-[#A1A1AA] text-xs mt-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(expense.shift_date), 'MMM dd')}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-[#A1A1AA]">
                      {expense.expense_date 
                        ? format(new Date(expense.expense_date), 'MMM dd, yyyy')
                        : format(new Date(expense.created_at), 'MMM dd, yyyy')
                      }
                    </TableCell>
                    <TableCell>
                      {expense.receipt_file_name ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openReceiptModal(expense)}
                          className="text-[#1ABC9C] hover:text-[#1ABC9C]"
                        >
                          <FileImage className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      ) : (
                        <span className="text-[#A1A1AA] text-sm">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLES[expense.status]}>
                        {expense.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {expense.status === 'pending' ? (
                        <Button
                          size="sm"
                          onClick={() => openReviewModal(expense)}
                          className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                          data-testid={`review-expense-${expense.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                      ) : (
                        <span className="text-xs text-[#A1A1AA]">
                          {expense.reviewer_name && `by ${expense.reviewer_name}`}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-md">
          <DialogHeader>
            <DialogTitle>Review Expense</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Review and approve or reject this expense claim
            </DialogDescription>
          </DialogHeader>
          {selectedExpense && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-[#1A1A1A] rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#A1A1AA]">Medic</span>
                  <span className="text-[#F2F2F2] font-medium">{selectedExpense.medic_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A1A1AA]">Category</span>
                  <span className="text-[#F2F2F2]">{selectedExpense.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A1A1AA]">Amount</span>
                  <span className="text-[#1ABC9C] font-bold font-['JetBrains_Mono']">
                    {fmt(selectedExpense.amount, selectedExpense.currency)}
                  </span>
                </div>
                {selectedExpense.shift_location && (
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Shift</span>
                    <span className="text-[#F2F2F2]">{selectedExpense.shift_location}</span>
                  </div>
                )}
                {selectedExpense.description && (
                  <div className="pt-2 border-t border-white/10">
                    <p className="text-[#A1A1AA] text-sm mb-1">Description</p>
                    <p className="text-[#F2F2F2]">{selectedExpense.description}</p>
                  </div>
                )}
              </div>

              {selectedExpense.receipt_file_name && (
                <Button
                  variant="outline"
                  className="w-full border-white/10"
                  onClick={() => {
                    setShowReviewModal(false);
                    openReceiptModal(selectedExpense);
                  }}
                >
                  <FileImage className="w-4 h-4 mr-2" />
                  View Receipt
                </Button>
              )}

              <div className="space-y-2">
                <Label>Review Notes {selectedExpense.status === 'pending' && '(required for rejection)'}</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about this expense..."
                  className="bg-[#1A1A1A] border-white/10"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => handleReject(selectedExpense.id)}
                  disabled={processing}
                  className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApprove(selectedExpense.id)}
                  disabled={processing}
                  className="flex-1 bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileImage className="w-5 h-5 text-[#1ABC9C]" />
              Receipt - {selectedExpense?.receipt_file_name}
            </DialogTitle>
          </DialogHeader>
          {selectedExpense && selectedExpense.receipt_file_name && (
            <div className="space-y-4">
              <div className="bg-[#1A1A1A] rounded-lg p-4 flex items-center justify-center min-h-[400px]">
                {selectedExpense.receipt_file_name.toLowerCase().endsWith('.pdf') ? (
                  <div className="text-center">
                    <File className="w-16 h-16 mx-auto mb-4 text-red-400" />
                    <p className="text-[#A1A1AA]">PDF Receipt</p>
                    <Button
                      variant="outline"
                      className="mt-4 border-white/10"
                      onClick={() => window.open(getReceiptUrl(selectedExpense), '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Open PDF
                    </Button>
                  </div>
                ) : (
                  <ReceiptImage 
                    src={getReceiptUrl(selectedExpense)} 
                    alt="Receipt"
                  />
                )}
              </div>
              <div className="flex justify-between items-center">
                <div className="text-sm text-[#A1A1AA]">
                  {selectedExpense.medic_name} • {fmt(selectedExpense.amount, selectedExpense.currency)} • {selectedExpense.category}
                </div>
                <Button
                  variant="outline"
                  className="border-white/10"
                  onClick={() => window.open(getReceiptUrl(selectedExpense), '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
